import { Link, useParams } from "react-router-dom";
import { Avatar, Box, Button, Chip, Container, Typography } from "@mui/material";
import { useBlogPost, useBlogList } from "../../../hooks/useBlog";
import { openSimplePracticeConsultation } from "../../../utils/simplePractice";
import { colors, fontFamilies } from "../../../constants/theme";
import SmartImage from "../../../components/SmartImage";
import Seo from "../../../components/Seo";
import { buildBlogPostSeo } from "../../../utils/seoBuilders";

const CARD_SHADOW = "0 6px 24px rgba(51, 38, 29, .06)";
const PILL_BG = "#F0ECE4";

const formatDate = (d) => {
  if (!d) return "";
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const RelatedCard = ({ post }) => (
  <Box
    component={Link}
    to={`/blog/${post.slug}`}
    sx={{
      display: "flex", flexDirection: "column", textDecoration: "none", color: "inherit",
      bgcolor: "#fff", borderRadius: "10px", boxShadow: CARD_SHADOW, p: 1.5, height: "100%",
      transition: "box-shadow .2s, transform .2s",
      "&:hover": { boxShadow: "0 12px 32px rgba(51, 38, 29, .12)", transform: "translateY(-2px)" },
    }}
  >
    {post.coverImage && (
      <SmartImage src={post.coverImage} alt={post.title} height={180} radius="16px" />
    )}
    <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
      {post.category && (
        <Chip label={post.category} size="small"
          sx={{
            mb: 1.5, alignSelf: "flex-start", bgcolor: PILL_BG, color: colors.primary,
            fontWeight: 600, fontFamily: fontFamilies.gotham, fontSize: 13, height: 34, px: 0.5, borderRadius: "33px",
          }} />
      )}
      <Typography sx={{ fontFamily: fontFamilies.gotham, fontWeight: 500, mb: 1.5, color: colors.primary, fontSize: 18, lineHeight: 1.3 }}>
        {post.title}
      </Typography>
      <Typography sx={{ mt: "auto", fontFamily: fontFamilies.quicksand, color: colors.secondary, fontWeight: 700, fontSize: 14 }}>
        Read More →
      </Typography>
    </Box>
  </Box>
);

const BlogPost = () => {
  const { slug } = useParams();
  const { post, loading, notFound } = useBlogPost(slug);
  const { items: recent } = useBlogList({ page: 1 });
  const related = (recent || []).filter((p) => p.slug !== slug).slice(0, 3);

  if (loading) {
    return (
      <Box sx={{ py: 10, textAlign: "center" }}>
        <Typography sx={{ fontFamily: fontFamilies.quicksand, color: colors.textLight }}>Loading…</Typography>
      </Box>
    );
  }

  if (notFound || !post) {
    return (
      <Box sx={{ py: 10, textAlign: "center" }}>
        <Seo title="Post Not Found" description="This article could not be found." noindex />
        <Typography sx={{ mb: 2, fontFamily: fontFamilies.gotham, fontSize: 26, color: colors.primary }}>
          Post not found
        </Typography>
        <Button component={Link} to="/blog" variant="outlined"
          sx={{ borderColor: colors.secondary, color: colors.secondary, borderRadius: 10, textTransform: "none", fontFamily: fontFamilies.quicksand }}>
          ← Back to Blog
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Seo {...buildBlogPostSeo(post)} />
      {post.coverImage && (
        <SmartImage src={post.coverImage} alt={post.title} fit="contain" height={{ xs: 320, md: 560 }} bg="#EFEAE1" />
      )}
      <Container maxWidth="md" sx={{ pt: { xs: 4, md: 6 } }}>
        <Button component={Link} to="/blog" size="small"
          sx={{ color: colors.textLight, mb: 2, pl: 0, textTransform: "none", fontFamily: fontFamilies.quicksand }}>
          ← Back to Blog
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          {post.category && (
            <Chip label={post.category} size="small"
              sx={{
                bgcolor: PILL_BG, color: colors.primary, fontWeight: 600,
                fontFamily: fontFamilies.gotham, fontSize: 13, height: 34, px: 0.5, borderRadius: "33px",
              }} />
          )}
          <Typography sx={{ fontFamily: fontFamilies.quicksand, color: colors.textLight, fontSize: 13 }}>
            {formatDate(post.date)} · {post.readTime || 1} min read
          </Typography>
        </Box>

        <Typography
          component="h1"
          sx={{ fontFamily: fontFamilies.gotham, fontWeight: 400, mb: 2, lineHeight: 1.2, color: colors.primary, fontSize: { xs: 30, md: 42 } }}
        >
          {post.title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <Avatar src={post.avatar || undefined} sx={{ width: 32, height: 32, bgcolor: colors.secondary }}>
            {post.authorName?.[0] || "N"}
          </Avatar>
          <Typography sx={{ fontFamily: fontFamilies.quicksand, fontWeight: 500, color: colors.textLight, fontSize: 15 }}>
            {post.authorName}
          </Typography>
        </Box>

        {post.excerpt && (
          <Typography
            sx={{ my: 3, fontSize: 20, lineHeight: 1.6, color: colors.textLight, fontFamily: fontFamilies.quicksand }}>
            {post.excerpt}
          </Typography>
        )}

        {/* Body is backend-sanitized HTML. Prose styling for headings/lists/links/images. */}
        <Box
          sx={{
            color: colors.primary, fontFamily: fontFamilies.quicksand, fontSize: 18, lineHeight: 1.8,
            "& h1,& h2,& h3": {
              fontFamily: fontFamilies.gotham, fontWeight: 500, mt: 4, mb: 1.5, lineHeight: 1.3, color: colors.primary,
            },
            "& h1": { fontSize: 30 }, "& h2": { fontSize: 26 }, "& h3": { fontSize: 22 },
            "& p": { mb: 2.5 },
            "& ul,& ol": { pl: 3, mb: 2.5 },
            "& li": { mb: 1 },
            "& a": { color: colors.secondary, textDecoration: "underline", "&:hover": { color: colors.accent } },
            "& img": { maxWidth: "100%", borderRadius: 2, my: 2 },
            "& iframe": { width: "100%", aspectRatio: "16 / 9", border: 0, borderRadius: 2, my: 2 },
            "& blockquote": {
              borderLeft: `4px solid ${colors.secondary}`, pl: 2.5, ml: 0, fontStyle: "italic", color: colors.textLight,
            },
          }}
          dangerouslySetInnerHTML={{ __html: post.body }}
        />

        {/* CTA */}
        <Box sx={{ mt: 6, p: { xs: 3, md: 5 }, bgcolor: "#F1ECE3", borderRadius: 4, textAlign: "center" }}>
          <Typography
            sx={{ fontFamily: fontFamilies.gotham, fontWeight: 400, mb: 1, color: colors.primary, fontSize: { xs: 22, md: 28 } }}
          >
            Experience the work for yourself
          </Typography>
          <Typography sx={{ fontFamily: fontFamilies.quicksand, color: colors.textLight, mb: 3 }}>
            Book a free consultation and take the first step.
          </Typography>
          <Button variant="contained" disableElevation onClick={openSimplePracticeConsultation}
            sx={{ bgcolor: colors.secondary, px: 4, py: 1.25, borderRadius: 1.5, textTransform: "none", fontFamily: fontFamilies.quicksand, fontWeight: 600, "&:hover": { bgcolor: "#22344a" } }}>
            Start Free Consultation
          </Button>
        </Box>

        {/* Continue Reading */}
        {related.length > 0 && (
          <Box sx={{ mt: 8 }}>
            <Typography
              sx={{ fontFamily: fontFamilies.gotham, fontWeight: 400, mb: 3, color: colors.primary, fontSize: { xs: 24, md: 30 } }}
            >
              Continue Reading
            </Typography>
            <Box sx={{
              display: "grid", gap: 3,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
            }}>
              {related.map((p) => <RelatedCard key={p.slug} post={p} />)}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default BlogPost;
